import { Conditions, Fields, Operands, RuleCreationSource } from '@root/constants'
import { handleReturn, MutationFunction, PaginatedResponse, QueryFunction } from './root'
import { network } from '@root/utils'

export interface Rule {
  field: Fields
  condition: Conditions
  value: string | number | boolean | number[]
}

export interface RuleNode {
  operand: Operands
  rules?: Array<Rule | RuleNode>
}

export interface IAccountingRuleBlueprintEntry {
  account_id: number
  default_vat_code?: number
  default_vat_rate_label?: string
  amount: string
}

export interface IAccountingRuleBlueprint {
  debet_entries?: IAccountingRuleBlueprintEntry[]
  credit_entries?: IAccountingRuleBlueprintEntry[]
  expense_entries?: IAccountingRuleBlueprintEntry[]
}

export type AccrualDateMode =
  | 'invoice_date'
  | 'previous_month_end'
  | 'two_months_end'
  | 'delivery_date'

export interface IAccountingRule {
  id: number
  enabled: boolean
  name: string
  description: string
  contact_name?: string
  tag_ids?: number[]
  payment_account: number | null
  created_from: RuleCreationSource
  priority: number
  rule_node: RuleNode
  blueprint: IAccountingRuleBlueprint
  wait_for_attachment?: boolean
  attachment_exempt?: boolean
  accrual_date_mode?: AccrualDateMode | null
  workflow?: number | null
  workflow_node_uuid?: string | null
}

export interface ISimpleAccountingRule {
  id: number
  name: string
}

interface CreateAccountingRuleProps {
  slug: string
  skipMatching?: boolean
}

export const createAccountingRule: MutationFunction<
  CreateAccountingRuleProps,
  IAccountingRule
> = async ({ slug, skipMatching }, requestData) => {
  const params = skipMatching ? { skip_matching: true } : undefined
  const url = `/v1/business/${slug}/automation/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    params,
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface UpdateAccountingRuleProps {
  slug: string
  id: number
  skipMatching?: boolean
}

export const updateAccountingRule: MutationFunction<
  UpdateAccountingRuleProps,
  IAccountingRule
> = async ({ slug, id, skipMatching }, requestData) => {
  const params = skipMatching ? { skip_matching: true } : undefined
  const url = `/v1/business/${slug}/automation/${id}/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'PATCH',
    params,
    data: requestData,
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

interface FetchAccountingRulesProps {
  slug: string
}

export const deleteAccountingRule: MutationFunction<{ slug: string; id: number }, void> = async ({
  slug,
  id
}) => {
  const url = `/v1/business/${slug}/automation/${id}/`
  const { status } = await network.httpClient.request({
    url,
    method: 'DELETE',
    validateStatus: () => true
  })
  return handleReturn({ status, data: null })
}

export const fetchAccountingRules: QueryFunction<
  FetchAccountingRulesProps,
  PaginatedResponse<IAccountingRule>
> = async ({ slug }, params = null) => {
  const url = `/v1/business/${slug}/automation/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET',
    params
  })
  return data
}

export const fetchAccountingRule: QueryFunction<
  { slug: string; id: number },
  IAccountingRule
> = async ({ slug, id }) => {
  const url = `/v1/business/${slug}/automation/${id}/`
  const { data } = await network.httpClient.request({
    url,
    method: 'GET'
  })
  return data
}

export interface AutomationRulePreviewTransaction {
  id: number
  date: string
  amount: number | null
  description: string | null
  contact_hint: string | null
  reference_number: string | null
  data_source: string | null
  payment_account: number | null
  document_id: number | null
}

export interface AutomationRulePreviewResult {
  count: number
  next: number | null
  prev: number | null
  size: number
  results: AutomationRulePreviewTransaction[]
}

export const fetchAutomationRulePreview = async ({
  slug,
  ruleId,
  page = 1
}: {
  slug: string
  ruleId: number
  page?: number
}): Promise<AutomationRulePreviewResult> => {
  const url = `/v1/business/${slug}/automation/${ruleId}/preview/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'GET',
    params: { page },
    validateStatus: () => true,
    timeout: 30000
  })
  return handleReturn({ status, data })
}

export interface BlueprintBalanceEntry {
  account_id?: number | null
  amount: string
}

export interface BlueprintBalanceResult {
  debet_total: number
  credit_total: number
  difference: number
  balanced: boolean
  sample_subunits: number
}

export const fetchBlueprintBalance = async ({
  slug,
  debetEntries,
  creditEntries
}: {
  slug: string
  debetEntries: BlueprintBalanceEntry[]
  creditEntries: BlueprintBalanceEntry[]
}): Promise<BlueprintBalanceResult> => {
  const url = `/v1/business/${slug}/automation/blueprint-balance/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: {
      debet_entries: debetEntries,
      credit_entries: creditEntries
    },
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export const applyAccountingRule = async ({
  slug,
  ruleId
}: {
  slug: string
  ruleId: number
}): Promise<void> => {
  const url = `/v1/business/${slug}/automation/${ruleId}/apply/`
  const { data, status } = await network.httpClient.request({
    url,
    method: 'POST',
    validateStatus: () => true
  })
  return handleReturn({ status, data })
}

export interface SwapAccountingRulePriorityProps {
  slug: string
  ruleId: number
  targetRuleId: number
}

export const swapAccountingRulePriority: MutationFunction<
  SwapAccountingRulePriorityProps,
  void
> = async ({ slug, ruleId, targetRuleId }) => {
  const url = `/v1/business/${slug}/automation/${ruleId}/swap-priority/`
  const { status } = await network.httpClient.request({
    url,
    method: 'POST',
    data: { target_rule_id: targetRuleId },
    validateStatus: () => true
  })
  return handleReturn({ status, data: null })
}
